import { createContext, useContext, useMemo } from "react";
import { FCC, TOption } from "@/types/common";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { useEnum } from "@/api/services/global/enum";
import {
  useGetLanguageTestList,
  useGetMajorList,
  useGetNationalityList,
  useGetResidenceStatusList,
  useGetSpecificSkillCertificateList,
} from "@/api/services/global/master";
import { TALENT_LOCATION_OPTIONS } from "@/types/enum";
import { CurrentOptionsKey } from "@/features/talent/components/talent-scan-image/current-value/format-current-value";

// Holds the snapshot of the talent at the moment the scan modal was opened from
// talent detail. Used by FormField to render `現在の値：X` helper text on every
// scan-modal input so users can compare AI-extracted values against the
// currently-saved record before persisting changes.
//
// Provider also pre-loads every option list referenced by select / selectMulti
// inputs so FormField can resolve id → label without re-fetching per field.
type CurrentTalentContextValue = {
  currentTalent: TTalentForm | null;
  options: Partial<Record<CurrentOptionsKey, TOption[]>>;
};

const EMPTY_VALUE: CurrentTalentContextValue = {
  currentTalent: null,
  options: {},
};

const CurrentTalentContext = createContext<CurrentTalentContextValue>(EMPTY_VALUE);

export const useCurrentTalent = () => useContext(CurrentTalentContext);

type ProviderProps = {
  currentTalent: TTalentForm | null;
};

export const CurrentTalentProvider: FCC<ProviderProps> = ({ currentTalent, children }) => {
  const { nationalityOptions } = useGetNationalityList();
  const { data: genderOptions } = useEnum("EGender");
  const { data: recruitmentStatusOptions } = useEnum("ERecruitmentStatus");
  const { residenceStatusOptions } = useGetResidenceStatusList();
  const { specificSkillCertificateOptions } = useGetSpecificSkillCertificateList();
  const { majorOptions, getJobTypeListOptionByMajor } = useGetMajorList();
  const { languageTestLevelOptions, languageTestOptions } = useGetLanguageTestList();

  // jobType options are major-dependent. Precompute from the snapshot's majorId
  // so the helper text resolves the saved jobTypeId regardless of any new
  // majorId the AI scan may suggest. Accepts stale if user changes major mid-form.
  const jobTypeOptions = useMemo(() => {
    if (currentTalent?.majorId == null) return [];
    return getJobTypeListOptionByMajor(Number(currentTalent.majorId)) ?? [];
  }, [currentTalent?.majorId, getJobTypeListOptionByMajor]);

  const value = useMemo<CurrentTalentContextValue>(
    () => ({
      currentTalent,
      options: {
        nationality: nationalityOptions ?? [],
        gender: genderOptions ?? [],
        certificate: specificSkillCertificateOptions ?? [],
        major: majorOptions ?? [],
        languageTestLevel: languageTestLevelOptions ?? [],
        languageTest: languageTestOptions ?? [],
        jobType: jobTypeOptions,
        location: TALENT_LOCATION_OPTIONS,
        recruitmentStatus: recruitmentStatusOptions ?? [],
        residenceStatus: residenceStatusOptions ?? [],
      },
    }),
    [
      currentTalent,
      nationalityOptions,
      genderOptions,
      specificSkillCertificateOptions,
      majorOptions,
      languageTestLevelOptions,
      languageTestOptions,
      jobTypeOptions,
      recruitmentStatusOptions,
      residenceStatusOptions,
    ],
  );

  return <CurrentTalentContext.Provider value={value}>{children}</CurrentTalentContext.Provider>;
};
